import { useKestrel } from 'features/kestrelViewer/KestrelProvider'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { WeatherWidget } from 'features/kestrelViewer/ui/tools/WeatherWidget'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { WorldPosition } from 'types'

import { KestrelModal } from './KestrelModal'

const InfoContainer = styled.div`
  margin-bottom: 0.75rem;
  text-align: left;
`

const InfoTitle = styled.p`
  font-weight: bold;
  width: 100%;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid white;
`

const InfoValue = styled.p``

type FlightMetadata = {
  [key: string]: string
}

export const InfoModal: React.FC = () => {
  const { infoOpen, setInfoOpen } = useKestrelViewerContext()
  const { kestrelModel } = useKestrel()

  const flightMetadata = useMemo<FlightMetadata>(() => {
    const data: FlightMetadata = {}

    if (
      kestrelModel?.data.boundingSegments &&
      kestrelModel.data.measurementScale
    ) {
      const ueToMeters = kestrelModel.data.measurementScale / 100
      const meterToFeet = 3.28084

      let perimeter = 0
      let area = 0

      const dist = (p1: WorldPosition, p2: WorldPosition) => {
        return Math.sqrt(
          Math.pow(p2.x - p1.x, 2) +
            Math.pow(p2.y - p1.y, 2) +
            Math.pow(p2.z - p1.z, 2)
        )
      }

      const segments = kestrelModel.data.boundingSegments
      for (let i = 0; i < segments.length - 1; i++) {
        perimeter += dist(segments[i], segments[i + 1])
        area +=
          0.5 *
          Math.abs(
            segments[i].x * segments[i + 1].y -
              segments[i + 1].x * segments[i].y
          )
      }

      const surfaceAreaMeters = area * ueToMeters * ueToMeters
      const surfaceAreaFeet = surfaceAreaMeters * meterToFeet * meterToFeet

      const perimeterMeters = perimeter * ueToMeters
      const perimeterFeet = perimeterMeters * meterToFeet

      data['Approximate Surface Area'] =
        `${surfaceAreaFeet.toFixed(2)}ft\u00B2 (${surfaceAreaMeters.toFixed(2)}m\u00B2)`
      data['Approximate Perimeter'] =
        `${perimeterFeet.toFixed(2)}ft (${perimeterMeters.toFixed(2)}m)`
    }

    return data
  }, [kestrelModel])

  const haveMetadata = useMemo(
    () => Object.entries(flightMetadata).length > 0,
    [flightMetadata]
  )

  const hasWeatherData = useMemo(
    () =>
      !!(
        kestrelModel?.data.scanDate &&
        kestrelModel?.data.origin?.lat &&
        kestrelModel?.data.origin?.long
      ),
    [kestrelModel]
  )

  if (!infoOpen) return null

  const handleClose = () => {
    setInfoOpen(false)
  }

  return (
    <KestrelModal title='Flight Information' handleClose={handleClose}>
      {hasWeatherData ? (
        <WeatherWidget
          flightDate={kestrelModel?.data.scanDate}
          latitude={kestrelModel?.data.origin?.lat}
          longitude={kestrelModel?.data.origin?.long}
        />
      ) : (
        <InfoContainer>
          <InfoValue>
            Weather data unavailable (missing flight date or location
            coordinates)
          </InfoValue>
        </InfoContainer>
      )}
      {haveMetadata
        ? Object.entries(flightMetadata).map(([title, value]) => (
            <InfoContainer key={title}>
              <InfoTitle>{title}</InfoTitle>
              <InfoValue>{value}</InfoValue>
            </InfoContainer>
          ))
        : !hasWeatherData && (
            <InfoContainer>
              <InfoValue>No flight information to show at this time</InfoValue>
            </InfoContainer>
          )}
    </KestrelModal>
  )
}
